import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosRiskInstance as axios } from "axios.js";
import dayjs from "dayjs";

const INITIAL_STATE = {
  loading: false,

  filters: {
    uan: [],
    hour: [],
    battery_dispatch_type: "SELF_CONSUMPTION",
    net_load_component: "NO_REG",
    net_dispatch_types: ["self_consumption"]
  },

  appliedFilters: {
    uan: [],
    start_date: "",
    end_date: "",
    hour: [],
    battery_dispatch_type: "SELF_CONSUMPTION",
    net_load_component: "NO_REG",
    net_dispatch_types: ["self_consumption"],
    summary: "HOUR"
  },

  date_range: {
    min_date: "",
    max_date: ""
  },

  chartData: [],
  filterStatus: false
};

// ====== INITIAL DATA FETCH ======
export const getBessDispatchInitialDataAsync = createAsyncThunk(
  "bessDispatch/Initial",
  async () => {
    const response = await axios.get(
      "/api/v1/solar_forecast/get_bess_dispatch_forecast"
    );
    return response.data;
  }
);

// ====== FILTERED DATA FETCH ======
export const getUpdatedBessDispatchDataAsync = createAsyncThunk(
  "bessDispatch/Updated",
  async ({ params }) => {
    const searchParams = new URLSearchParams();
// Add Hour array
    params.hour?.forEach(h => searchParams.append("hour", h));
    // Add Start/End Dates
    if (params.start_date) {
      searchParams.append("start_date", dayjs(params.start_date).format("YYYY-MM-DD"));
    }
    if (params.end_date) {
      searchParams.append("end_date", dayjs(params.end_date).format("YYYY-MM-DD"));
    }

    // Add battery dispatch type and net load component
    if (params.battery_dispatch_type) {
      searchParams.append("battery_dispatch_type", params.battery_dispatch_type);
    }
    if (params.net_load_component) {
      searchParams.append("net_load_component", params.net_load_component);
    }
    if (params.summary) {
      searchParams.append("summary", params.summary);
    }

    const response = await axios.get(
      `/api/v1/solar_forecast/get_bess_dispatch_forecast?${searchParams.toString()}`
    );

    // Return data with net_dispatch_types for frontend filtering
    return {
      ...response.data,
      applied_filters: {
        ...response.data.applied_filters,
        net_dispatch_types: params.net_dispatch_types || []
      }
    };
  }
);


export const bessDispatchSlice = createSlice({
  name: "bessDispatch",
  initialState: INITIAL_STATE,

  reducers: {
    setBessAppliedFilters: (state, action) => {
      state.appliedFilters = { ...state.appliedFilters, ...action.payload };
      state.filterStatus = true;
    },
    resetBessFilters: (state) => {
      state.filterStatus = false;
    }
  },

  extraReducers: (builder) => {
    builder
      // ===== INITIAL =====
      .addCase(getBessDispatchInitialDataAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBessDispatchInitialDataAsync.fulfilled, (state, action) => {
        state.loading = false;

        const res = action.payload;

        state.chartData = res.data || [];
        state.filters = res.filters || {};
        state.date_range = res.date_range || { min_date: "", max_date: "" };
        state.appliedFilters = res.applied_filters || state.appliedFilters;
        state.filterStatus = false; // reset
      })
      .addCase(getBessDispatchInitialDataAsync.rejected, (state) => {
        state.loading = false;
      })

      // ===== UPDATED =====
      .addCase(getUpdatedBessDispatchDataAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUpdatedBessDispatchDataAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.chartData = action.payload.data || [];
        state.filterStatus = false; // reset to prevent re-triggering
      })
      .addCase(getUpdatedBessDispatchDataAsync.rejected, (state) => {
        state.loading = false;
        state.filterStatus = false;
      });
  }
});

export const { setBessAppliedFilters, resetBessFilters } = bessDispatchSlice.actions;
export default bessDispatchSlice.reducer;
